import { getHexTileMapKey } from '../hexmap/model/keys';
import type { HexTileMapKey } from '../hexmap/model/types';
import type { BuildingDef, RuleSet } from '../rules/schema';
import { toMilli } from './fixed';
import { buildingLevel, levelStat } from './levels';
import { holdsStock } from './stock';
import { mintOrder, territoryOwners } from './territory';
import type { Building, EntityId, PlayerId, SimState } from './types';

interface CityBonus {
  hp: number;
  strength: number;
  /** Extra vision radius (a watchtower's `cityVision`). */
  vision: number;
}

/** Whether buildings of this def boost the city they stand in. */
function boostsCity(def: BuildingDef): boolean {
  return (
    def.cityHp !== undefined ||
    def.cityStrength !== undefined ||
    def.cityVision !== undefined ||
    (def.levels ?? []).some((l) => l.cityVision !== undefined)
  );
}

/**
 * The city `b` boosts: the city whose territory `b` stands in, if it's `b`'s
 * owner's. Undefined for buildings that give no city bonus.
 */
function bonusCity(
  state: SimState,
  rules: RuleSet,
  b: Building,
  owners?: ReadonlyMap<HexTileMapKey, EntityId>
): Building | undefined {
  const def = rules.buildings[b.type];
  if (!def || !boostsCity(def)) return undefined;
  const cityId = (owners ?? territoryOwners(state, rules)).get(getHexTileMapKey(b.pos));
  const city = cityId === undefined ? undefined : state.buildings[cityId];
  return city?.owner === b.owner ? city : undefined;
}

/**
 * City id → the summed level bonuses of the completed buildings (barracks)
 * in its territory. Derived from state each call, so a destroyed building's
 * bonus is gone at once. Only the oldest `maxPerCity` of a type count per
 * city, so a new city taking another's barracks tile can't stack bonuses.
 * Pass `knownOwners` (from `territoryOwners`) to skip recomputing territory.
 */
function cityBonuses(
  state: SimState,
  rules: RuleSet,
  knownOwners?: ReadonlyMap<HexTileMapKey, EntityId>
): Map<EntityId, CityBonus> {
  const result = new Map<EntityId, CityBonus>();
  const counted = new Map<string, number>();
  let owners = knownOwners;
  const buildings = Object.values(state.buildings).sort(
    (a, b) => mintOrder(a.id) - mintOrder(b.id) || (a.id < b.id ? -1 : 1)
  );
  for (const b of buildings) {
    const def = rules.buildings[b.type];
    if (!def || b.buildProgress < 1) continue;
    if (!boostsCity(def)) continue;
    owners ??= territoryOwners(state, rules);
    const city = bonusCity(state, rules, b, owners);
    if (!city) continue;
    const countKey = `${city.id}:${b.type}`;
    const count = counted.get(countKey) ?? 0;
    if (def.maxPerCity !== undefined && count >= def.maxPerCity) continue;
    counted.set(countKey, count + 1);
    const level = buildingLevel(b);
    const bonus = result.get(city.id) ?? { hp: 0, strength: 0, vision: 0 };
    bonus.hp += levelStat(def, level, 'cityHp') ?? 0;
    bonus.strength += levelStat(def, level, 'cityStrength') ?? 0;
    bonus.vision += levelStat(def, level, 'cityVision') ?? 0;
    result.set(city.id, bonus);
  }
  return result;
}

/**
 * Max hp of a building, in thousandths (fixed.ts): its level's hp plus any
 * bonus from buildings in its territory, raised by `pacing.lastStandHp` on a
 * last stand.
 */
function buildingMaxHp(
  state: SimState,
  rules: RuleSet,
  b: Building,
  bonuses: Map<EntityId, CityBonus> = cityBonuses(state, rules)
): number {
  const def = rules.buildings[b.type];
  if (!def) return b.hp;
  const hp = (levelStat(def, buildingLevel(b), 'hp') ?? def.hp) + (bonuses.get(b.id)?.hp ?? 0);
  return toMilli(b.lastStand ? hp * (1 + rules.pacing.lastStandHp) : hp);
}

/**
 * Damage per second a building deals: its level's strength plus any bonus
 * from buildings in its territory, times `pacing.lastStandFire` on a last
 * stand. 0 for buildings that don't fire.
 */
function buildingStrength(
  rules: RuleSet,
  b: Building,
  bonuses: ReadonlyMap<EntityId, CityBonus>
): number {
  const def = rules.buildings[b.type];
  const base = def ? (levelStat(def, buildingLevel(b), 'strength') ?? 0) : 0;
  if (base <= 0) return 0;
  const strength = base + (bonuses.get(b.id)?.strength ?? 0);
  return b.lastStand ? strength * rules.pacing.lastStandFire : strength;
}

/** A building's attack reach at its level (1 unless its rules say otherwise). */
function buildingRange(rules: RuleSet, b: Building): number {
  const def = rules.buildings[b.type];
  return (def ? levelStat(def, buildingLevel(b), 'range') : undefined) ?? 1;
}

/**
 * Marks each player's only completed city as on a last stand (`lastStand`)
 * and clears the mark once they have another. Entering and leaving scale hp
 * with the max, so the hp fraction is kept (founding and losing a second
 * city never heals the first). Every player starts with one city, so a
 * starting city is on a last stand until its owner founds another.
 */
function syncLastStand(state: SimState, rules: RuleSet): void {
  const cities = new Map<PlayerId, number>();
  for (const b of Object.values(state.buildings)) {
    if (holdsStock(rules, b)) cities.set(b.owner, (cities.get(b.owner) ?? 0) + 1);
  }
  let bonuses: Map<EntityId, CityBonus> | undefined;
  for (const b of Object.values(state.buildings)) {
    // Comeback help only: a player who has lost a city, down to their last one.
    const want =
      holdsStock(rules, b) &&
      cities.get(b.owner) === 1 &&
      state.players[b.owner]?.lostCity === true;
    if (want === !!b.lastStand) continue;
    bonuses ??= cityBonuses(state, rules);
    const before = buildingMaxHp(state, rules, b, bonuses);
    if (want) b.lastStand = true;
    else delete b.lastStand;
    if (before > 0) b.hp = Math.round((b.hp * buildingMaxHp(state, rules, b, bonuses)) / before);
  }
}

/** Clamps every building's hp to its max (after a bonus building falls). */
function clampHp(state: SimState, rules: RuleSet): void {
  const bonuses = cityBonuses(state, rules);
  for (const b of Object.values(state.buildings)) {
    b.hp = Math.min(b.hp, buildingMaxHp(state, rules, b, bonuses));
  }
}

export type { CityBonus };
export {
  bonusCity,
  buildingMaxHp,
  buildingRange,
  buildingStrength,
  cityBonuses,
  clampHp,
  syncLastStand,
};
